import { useQuery } from '@tanstack/react-query'
import { Rocket } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { ErrorState, LoadingState } from '@/components/feedback/States'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { setupQuery, useSetupVisibility } from '@/features/setup/api'
import { SetupChecklistView } from '@/features/setup/SetupChecklistView'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function SetupGuidePage() {
  const { t } = useTranslation('setup')
  useDocumentTitle('Setup guide')
  const setup = useQuery(setupQuery())
  const visibility = useSetupVisibility()

  return (
    <>
      <PageHeader
        title={t('Setup guide')}
        description={t('The steps to get your school running. Progress updates as you add your data.')}
        breadcrumbs={[{ label: t('Administration') }, { label: t('Setup guide') }]}
        homeHref="/admin"
        actions={
          setup.data && !setup.data.complete ? (
            <Button
              variant="secondary"
              loading={visibility.isPending}
              onClick={() => visibility.mutate(!setup.data.dismissed)}
            >
              {setup.data.dismissed ? t('Show on the dashboard') : t('Hide from the dashboard')}
            </Button>
          ) : undefined
        }
      />
      {setup.isPending ? (
        <LoadingState />
      ) : setup.error ? (
        <ErrorState onRetry={() => void setup.refetch()} />
      ) : (
        <div className="space-y-6">
          {setup.data.complete ? (
            <Alert tone="success" title={t('Your school is set up')}>
              {t('Every essential step is done. The optional ones can wait until you need them.')}
            </Alert>
          ) : null}
          <Card className="overflow-hidden">
            <CardHeader
              icon={<Rocket />}
              title={t('{{done}} of {{total}} essential steps done', {
                done: setup.data.done,
                total: setup.data.total,
              })}
            />
            <div className="h-1.5 bg-surface-sunken" aria-hidden="true">
              <div
                className="h-full bg-brand-500 transition-all"
                style={{ width: `${(setup.data.done / setup.data.total) * 100}%` }}
              />
            </div>
            <SetupChecklistView checklist={setup.data} />
          </Card>
        </div>
      )}
    </>
  )
}
